/**
 * HighlightedReferencesContext - manages highlighted reference state
 *
 * Extracted from AIContext to break circular dependency between
 * AIContext, SuggestedContext, and EditorContext
 */
import { createContext, useContext, ReactNode, useState, useMemo } from 'react';
import { UUID } from '@annote/types';

export interface HighlightedReference {
  noteId: UUID;
  textStart: number;
  textEnd: number;
}

interface HighlightedReferencesContextType {
  highlightedReferences: HighlightedReference[];
  setHighlightedReferences: (refs: HighlightedReference[]) => void;
}

const HighlightedReferencesContext = createContext<HighlightedReferencesContextType | null>(null);

interface HighlightedReferencesProviderProps {
  children: ReactNode;
}

export const HighlightedReferencesProvider: React.FC<HighlightedReferencesProviderProps> = ({ children }) => {
  const [highlightedReferences, setHighlightedReferences] = useState<HighlightedReference[]>([]);

  const value = useMemo(
    () => ({
      highlightedReferences,
      setHighlightedReferences,
    }),
    [highlightedReferences],
  );

  return <HighlightedReferencesContext.Provider value={value}>{children}</HighlightedReferencesContext.Provider>;
};

export const useHighlightedReferences = (): HighlightedReferencesContextType => {
  const context = useContext(HighlightedReferencesContext);
  if (!context) {
    throw new Error('useHighlightedReferences must be used within a HighlightedReferencesProvider');
  }
  return context;
};
